import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, MicIcon, ImageIcon, PaperclipIcon, ArrowUpIcon, ArrowRightIcon, ChevronDownIcon, GlobeIcon, UserIcon, ShirtIcon, LayersIcon, FootprintsIcon, ShoppingBagIcon, RotateCcwIcon, TruckIcon, RulerIcon, ClockIcon, ZapIcon, CalendarIcon, ScaleIcon } from 'lucide-react';
import { DeckLogoPlate } from './DeckLogo';
import { APPAREL_BENEFITS, APPAREL_FEATURED, APPAREL_SUGGESTIONS } from '../../data/deck';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Page offset that frames the ask composer for the typing beat. */
export const APPAREL_ASK_SCROLL = 360;
const CATEGORIES = [{
  label: 'Men',
  icon: UserIcon
}, {
  label: 'Women',
  icon: UserIcon
}, {
  label: 'Denim',
  icon: LayersIcon
}, {
  label: 'Shirts',
  icon: ShirtIcon
}, {
  label: 'Outerwear',
  icon: ShoppingBagIcon
}, {
  label: 'Shoes',
  icon: FootprintsIcon
}];
const BENEFIT_ICONS = [RotateCcwIcon, TruckIcon, RulerIcon, ClockIcon];

/**
 * The merchant's own brand lockup — a couture serif wordmark with hairline
 * rules and a house line, the way a luxury fashion house signs its store.
 */
export function NoirWordmark({
  size = 'lg'
}: {
  size?: 'sm' | 'lg';
}) {
  const large = size === 'lg';
  return <span className="inline-flex flex-col items-center leading-none" data-id="element-1609" data-mp="2gk3hk40l6jir">
      <span className="text-dark-navy" style={{
      fontFamily: 'var(--font-couture)',
      fontWeight: 400,
      fontSize: large ? 34 : 17,
      letterSpacing: large ? '0.42em' : '0.34em',
      textIndent: large ? '0.42em' : '0.34em'
    }} data-id="element-1610" data-mp="34apfv3ksgpfk">
        NOIR
      </span>
      {large ? <span className="flex items-center gap-2 mt-2" data-id="element-1611" data-mp="307jbk1h8l8bq">
          <span className="w-8 h-px bg-dark-navy/25" data-id="element-1612" data-mp="1juvxz1jj2khh" />
          <span className="text-[7.5px] uppercase tracking-[0.38em] text-dark-navy/50" data-id="element-1613" data-mp="1jt1dtpg2yweg">
            Maison · Dubai
          </span>
          <span className="w-8 h-px bg-dark-navy/25" data-id="element-1614" data-mp="3qiv7fmp2fhiy" />
        </span> : <span className="mt-1 text-[5.5px] uppercase tracking-[0.34em] text-dark-navy/45" data-id="element-1615" data-mp="3qkz87vro40f0">
          Maison · Dubai
        </span>}
    </span>;
}
interface ApparelShopPageProps {
  /**
   * 'top' holds the page at the hero. 'browse' plays one continuous read of
   * the whole page — glide, pause, glide — ending exactly at the footer.
   */
  mode?: 'top' | 'browse';
  browseDuration?: number;
  /** Lets the window finish expanding before the page starts moving. */
  browseDelay?: number;
  /** Text currently in the composer. */
  typed?: string;
  focused?: boolean;
  caret?: boolean;
  /** Category the customer is hovering/selecting in the ribbon. */
  activeCategory?: string;
}

/**
 * The merchant's own Talab-hosted storefront (talab.app/shop/noir), rendered
 * as one tall page so the slide can scroll through it top to bottom before the
 * customer starts a conversation.
 */
export function ApparelShopPage({
  mode = 'top',
  browseDuration = 10,
  browseDelay = 0,
  typed = '',
  focused = false,
  caret = false,
  activeCategory
}: ApparelShopPageProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxScroll, setMaxScroll] = useState(0);

  // Measure the real page height so the browse always lands on the footer,
  // however tall the content ends up at the current window size.
  useLayoutEffect(() => {
    const measure = () => {
      const viewport = viewportRef.current;
      const content = contentRef.current;
      if (!viewport || !content) return;
      setMaxScroll(Math.max(content.scrollHeight - viewport.clientHeight, 0));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (viewportRef.current) observer.observe(viewportRef.current);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);
  const browseKeyframes = [0, -maxScroll * 0.33, -maxScroll * 0.33, -maxScroll * 0.66, -maxScroll * 0.66, -maxScroll, -maxScroll];
  return <div ref={viewportRef} className="absolute inset-0 overflow-hidden bg-slate-50" data-id="element-1616" data-mp="39zhee2klflju">
      <motion.div ref={contentRef} animate={{
      y: mode === 'browse' ? browseKeyframes : 0
    }} transition={mode === 'browse' ? {
      duration: browseDuration,
      delay: browseDelay,
      times: [0, 0.2, 0.32, 0.54, 0.66, 0.9, 1],
      ease: 'easeInOut'
    } : {
      duration: 1.6,
      ease: EASE
    }} data-id="element-1617" data-mp="3nyyvdvrnduin">
        {/* Talab shop top bar */}
        <div className="flex items-center justify-between h-11 px-5" data-id="element-1618" data-mp="bj4y3pmqvvrd">
          <DeckLogoPlate data-id="element-1619" data-mp="6ja7zoasna7d" />
          <div className="flex items-center gap-2" data-id="element-1620" data-mp="6k23z08az9y6">
            <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-[10px] text-slate-600" data-id="element-1621" data-mp="kgyqwqlaw79s">
              <GlobeIcon className="w-3 h-3 text-slate-400" data-id="element-1622" data-mp="fgkkte39f7ee" /> English
              <ChevronDownIcon className="w-2.5 h-2.5 text-slate-300" data-id="element-1623" data-mp="z5qs49q2i20q" />
            </span>
            <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-[10px] text-slate-600" data-id="element-1624" data-mp="142h86x1vgxm5">
              <span className="w-3.5 h-2.5 rounded-[2px] bg-gradient-to-b from-success to-dark-navy" data-id="element-1625" data-mp="q5u0bz8dsn1g" />
              UAE
              <ChevronDownIcon className="w-2.5 h-2.5 text-slate-300" data-id="element-1626" data-mp="ge7pw53varzr" />
            </span>
            <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-[10px] text-slate-600" data-id="element-1627" data-mp="l4o2jmzv69l2">
              <UserIcon className="w-3 h-3 text-slate-400" data-id="element-1628" data-mp="6gt8a267jx5v" /> Sign in
            </span>
          </div>
        </div>

        {/* Hero */}
        <div className="flex flex-col items-center pt-5 px-10 text-center" data-id="element-1629" data-mp="bcs4gbxs2jgu">
          <div className="p-[5px] rounded-2xl bg-white/70 border border-slate-200/70" data-id="element-1630" data-mp="3tkzpfraknfnr">
            <div className="relative flex items-center justify-center w-[236px] h-[86px] rounded-xl bg-white border border-slate-100 shadow-sm" data-id="element-1631" data-mp="2cv9jsksoodc">
              {/* Hairline inner rule, as on a boutique plaque */}
              <span className="absolute inset-[7px] rounded-lg border border-dark-navy/[0.07]" data-id="element-1632" data-mp="1zb2ofji8gsac" />
              <NoirWordmark data-id="element-1633" data-mp="1ue2fn0u01bfj" />
            </div>
          </div>
          <span className="-mt-2 px-2.5 py-0.5 rounded-full bg-white border border-slate-200 text-[9px] text-slate-400" data-id="element-1634" data-mp="1qch71rohzjb5">
            talab.app/shop/noir
          </span>

          <h1 className="mt-3 font-display font-bold text-[30px] leading-tight tracking-tight text-dark-navy" data-id="element-1635" data-mp="2mzu8fnlo64c1">
            Ask{' '}
            <span className="text-primary-indigo" style={{
            fontFamily: 'var(--font-couture)',
            fontWeight: 500,
            letterSpacing: '0.1em'
          }} data-id="element-1636" data-mp="2e0qpcjmi5oxj">
              NOIR
            </span>{' '}
            Anything
          </h1>
          <p className="mt-2 max-w-[440px] text-[11.5px] leading-relaxed text-slate-500" data-id="element-1637" data-mp="12ka7kjnh6w7i">
            Find, style and buy with AI — from denim and shirts to outerwear, footwear and
            accessories.
          </p>
          <p className="mt-3 flex items-center gap-1.5 text-[8.5px] font-bold uppercase tracking-[0.18em] text-primary-indigo" data-id="element-1638" data-mp="3e9gnn0jf6me2">
            <SparklesIcon className="w-2.5 h-2.5" data-id="element-1639" data-mp="3ia8xnw048vbe" /> Powered by talab.app
          </p>
          <p className="mt-0.5 text-[7.5px] uppercase tracking-[0.2em] text-slate-300" data-id="element-1640" data-mp="3n2jz53ai8p62">
            AI conversational ecommerce
          </p>
        </div>

        {/* Category ribbon */}
        <div className="px-10 mt-5" data-id="element-1641" data-mp="uk0wxxdgyng">
          <div className="flex items-stretch w-full p-1.5 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-1642" data-mp="tin4loz1y21">
            {CATEGORIES.map((category, index) => {
            const on = activeCategory === category.label;
            return <motion.div key={category.label} className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl ${index > 0 ? 'border-l border-slate-100' : ''}`} animate={{
              backgroundColor: on ? 'rgba(67,56,202,0.06)' : 'rgba(255,255,255,0)'
            }} transition={{
              duration: 0.35,
              ease: EASE
            }} data-id="element-1643" data-mp="27l9so7q7odm3" data-mpi={index}>
                  <category.icon className={`w-3.5 h-3.5 transition-colors duration-300 ${on ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-1644" data-mp="21i521irp1fpy" />
                  <span className={`text-[8px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${on ? 'text-primary-indigo' : 'text-slate-500'}`} data-id="element-1645" data-mp="19tlexhhmupqa">
                    {category.label}
                  </span>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Ask composer */}
        <div className="px-16 mt-4" data-id="element-1646" data-mp="2p9aio05gkc1b">
          <div className="relative" data-id="element-1647" data-mp="2p7zisbf4vofh">
            <motion.div className="absolute -inset-[3px] rounded-[28px] bg-gradient-to-r from-primary-indigo/25 to-ai-purple/25 blur-md" animate={{
            opacity: focused ? 1 : 0
          }} transition={{
            duration: 0.4,
            ease: EASE
          }} aria-hidden="true" data-id="element-1648" data-mp="2xpjc9yldy00e" />
            <div className="relative bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.07)] p-2" data-id="element-1649" data-mp="2sq6lj2re88sj">
              <div className="rounded-[22px] px-4 py-4" data-id="element-1650" data-mp="3gh2als4gspw3">
                <div className="min-h-[20px] text-[13px]" data-id="element-1651" data-mp="3lf44z97ccbmv">
                  {typed ? <span className="text-dark-navy" data-id="element-1652" data-mp="3lgx0bsci79fk">{typed}</span> : <span className="text-slate-300" data-id="element-1653" data-mp="36o3lm9522xby">Ask NOIR anything...</span>}
                  {caret && <motion.span className="inline-block w-[1.5px] h-[14px] ml-0.5 -mb-[2px] bg-primary-indigo align-middle" animate={{
                  opacity: [1, 1, 0, 0]
                }} transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-1654" data-mp="39r23eq38o67" />}
                </div>
                <div className="flex items-center justify-between mt-4" data-id="element-1655" data-mp="nkroe2naw04t">
                  <span className="text-[8.5px] font-semibold uppercase tracking-[0.16em] text-primary-indigo/70" data-id="element-1656" data-mp="9kqddudbncbm">
                    Find · Style · Compare · Sizes · Delivery
                  </span>
                  <div className="flex items-center gap-1.5" data-id="element-1657" data-mp="3w0bamhpk0iad">
                    <MicIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-1658" data-mp="4mf2dgbaivrt" />
                    <ImageIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-1659" data-mp="4orugmarimil" />
                    <PaperclipIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-1660" data-mp="3n1zb1q6pma5s" />
                    <span className="ml-1 w-7 h-7 rounded-full bg-primary-indigo flex items-center justify-center" data-id="element-1661" data-mp="3r1hzux1nj5dw">
                      <ArrowUpIcon className="w-3.5 h-3.5 text-white" data-id="element-1662" data-mp="3d3tdtlvuls79" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Suggested prompts */}
        <div className="px-10 mt-5" data-id="element-1663" data-mp="2yhmj552br1ke">
          <p className="text-center text-[8px] font-semibold uppercase tracking-[0.2em] text-slate-400" data-id="element-1664" data-mp="2thi2nf1f26u6">
            Try asking something like
          </p>
          <div className="grid grid-cols-4 gap-2.5 mt-2.5" data-id="element-1665" data-mp="38bmliz681spf">
            {APPAREL_SUGGESTIONS.map(suggestion => <div key={suggestion} className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white border border-slate-100 rounded-2xl shadow-sm" data-id="element-1666" data-mp="3hee037ue94la">
                <span className="text-[10px] leading-snug text-slate-600" data-id="element-1667" data-mp="3r47a44ew1bnf">{suggestion}</span>
                <ArrowRightIcon className="w-3 h-3 text-slate-300 shrink-0" data-id="element-1668" data-mp="3m5dzqkuwe0rh" />
              </div>)}
          </div>
        </div>

        {/* Trust badges */}
        <div className="px-10 mt-5" data-id="element-1669" data-mp="400z6x14apdz">
          <div className="grid grid-cols-4 gap-2.5" data-id="element-1670" data-mp="3w3tle8b5ohtl">
            {APPAREL_BENEFITS.map((benefit, index) => {
            const Icon = BENEFIT_ICONS[index];
            return <div key={benefit.title} className="flex items-start gap-2.5 px-3 py-3 bg-white border border-slate-100 rounded-2xl shadow-sm" data-id="element-1671" data-mp="c0gob4ljajxb" data-mpi={index}>
                  <span className="w-6 h-6 rounded-lg bg-primary-indigo/[0.07] flex items-center justify-center shrink-0" data-id="element-1672" data-mp="lq9wrwqdqixc">
                    <Icon className="w-3 h-3 text-primary-indigo" data-id="element-1673" data-mp="psdeh33n6jr2" />
                  </span>
                  <div data-id="element-1674" data-mp="uqf5qbb81pdm">
                    <p className="text-[10px] font-semibold text-primary-indigo" data-id="element-1675" data-mp="upx2uvu20cwq">
                      {benefit.title}
                    </p>
                    <p className="text-[9px] leading-snug text-slate-400" data-id="element-1676" data-mp="zo6xn5bv9bht">{benefit.body}</p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Featured pieces */}
        <div className="px-10 mt-8" data-id="element-1677" data-mp="36g7secqbtu6i">
          <h2 className="text-center font-display font-bold text-[22px] text-dark-navy tracking-tight" data-id="element-1678" data-mp="36hag6bscrgi7">
            Featured Pieces
          </h2>
          <p className="text-center text-[10.5px] text-slate-500 mt-1" data-id="element-1679" data-mp="1iuo9ks8uf8lp">
            Popular styles available from NOIR
          </p>
          <div className="w-14 h-[3px] mx-auto mt-2.5 rounded-full bg-warning" data-id="element-1680" data-mp="1es9m1uu8bko3" />

          <div className="grid grid-cols-4 gap-3.5 mt-5" data-id="element-1681" data-mp="19uhx99ovseo4">
            {APPAREL_FEATURED.map(item => <div key={item.title} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col" data-id="element-1682" data-mp="26kijb0xv8h1q">
                <div className="relative aspect-[4/3] bg-slate-100" data-id="element-1683" data-mp="1xjal0ivcng7n">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-1684" data-mp="1xlnbjkf6aywb" />
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-warning text-dark-navy text-[7.5px] font-bold uppercase tracking-[0.1em]" data-id="element-1685" data-mp="m5ypcv9qu3wk">
                    {item.badge}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-3" data-id="element-1686" data-mp="2505v4j5fi0d0">
                  <p className="text-[7.5px] font-bold uppercase tracking-[0.16em] text-primary-indigo" data-id="element-1687" data-mp="29ua0ixz1q0bz">
                    {item.category}
                  </p>
                  <p className="mt-1 font-display font-bold text-[12px] text-dark-navy leading-tight" data-id="element-1688" data-mp="20zvp56hmz9y5">
                    {item.title}
                  </p>
                  <p className="mt-0.5 text-[9px] text-slate-400" data-id="element-1689" data-mp="2onvcdwfqoi7z">{item.spec}</p>
                  <div className="mt-2.5 pt-2.5 border-t border-slate-100" data-id="element-1690" data-mp="2sr1h2zn7e1cc">
                    <p className="font-display font-bold text-[13px] text-dark-navy leading-none" data-id="element-1691" data-mp="2eqqp1ta5qgpl">
                      {item.price}
                    </p>
                    <p className="mt-1 text-[8.5px] text-slate-400" data-id="element-1692" data-mp="13cmuolznqqk0">{item.secondary}</p>
                  </div>
                  <div className="mt-auto pt-2.5 space-y-1.5" data-id="element-1693" data-mp="18bgc2pi2f1pg">
                    <div className="flex items-center justify-center gap-1.5 h-7 rounded-lg bg-primary-indigo text-white text-[9.5px] font-semibold" data-id="element-1694" data-mp="18d12q419e0i1">
                      <SparklesIcon className="w-3 h-3" data-id="element-1695" data-mp="vtx4ecsjfmak" /> Ask about this piece
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1696" data-mp="19t57vhvqhgkk">
                      <span className="flex-1 flex items-center justify-center gap-1 h-6 rounded-lg border border-slate-200 text-[8.5px] text-slate-500" data-id="element-1697" data-mp="14tskmw0ya7hg">
                        <CalendarIcon className="w-2.5 h-2.5" data-id="element-1698" data-mp="h50wcfqhe99y" /> Sizes
                      </span>
                      <span className="flex-1 flex items-center justify-center gap-1 h-6 rounded-lg border border-slate-200 text-[8.5px] text-slate-500" data-id="element-1699" data-mp="d3xh9vg9ch9s">
                        <ScaleIcon className="w-2.5 h-2.5" data-id="element-1700" data-mp="m3kncujxmb96" /> Compare
                      </span>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Browse by category */}
        <div className="px-10 mt-8" data-id="element-1701" data-mp="3pjrg460t6o36">
          <h2 className="text-center font-display font-bold text-[20px] text-dark-navy tracking-tight" data-id="element-1702" data-mp="1th64o1jsgm4j">
            Browse by Category
          </h2>
          <p className="text-center text-[10.5px] text-slate-500 mt-1" data-id="element-1703" data-mp="1yeeriffvtec4">
            Pick a starting point and continue the conversation
          </p>
          <div className="flex items-center justify-center gap-2.5 mt-4" data-id="element-1704" data-mp="285j2l42fsf1o">
            {['Denim', 'Shirts', 'Outerwear', 'Shoes', 'Accessories'].map(chip => <span key={chip} className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-[10px] font-medium text-slate-600 shadow-sm" data-id="element-1705" data-mp="1ak50uk1z0s26">
                <ZapIcon className="w-2.5 h-2.5 text-slate-400" data-id="element-1706" data-mp="1ohtm3szya17r" />
                {chip}
              </span>)}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-5 pb-7 border-t border-slate-200 text-center" data-id="element-1707" data-mp="2vxg5u6rtlyr6">
          <div className="flex justify-center" data-id="element-1708" data-mp="2zvdth55y0csg">
            <NoirWordmark size="sm" data-id="element-1709" data-mp="o9dfd43q7iz1" />
          </div>
          <p className="mt-2.5 text-[10px] font-medium text-primary-indigo" data-id="element-1710" data-mp="o93oleaux7sa">
            NOIR Apparel Trading LLC
          </p>
          <p className="mt-1 text-[8.5px] uppercase tracking-[0.18em] text-slate-400" data-id="element-1711" data-mp="kae2mxbr6hx1">
            Powered by Talab
          </p>
        </div>
      </motion.div>
    </div>;
}